import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import useFeature from '~/store/clinic/hooks/useFeature';
import { useStoreActions, useStoreState } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error/error';
import { ICustomDictionaryType } from '~/services/api/dictionaries/types';
import ConfirmModal from '~/ui/components/common/ConfirmModal';
import Loader from '~/ui/components/common/Loader';
import { getAddLabel } from '~/ui/pages/Dictionaries/helpers/dictionaryType';
import useDictionaryItemAction from '~/ui/pages/Dictionaries/hooks/useDictionaryItemAction';
import dispositionDictionary from '~/ui/constants/dispositionDictionary';
import documentTypeDictionary from '~/ui/constants/documentTypeDictionary';
import exemptionCategoryDictionary from '~/ui/constants/exemptionCategoryDictionary';
import medicaidTypeDictionary from '~/ui/constants/medicaidTypeDictionary';
import AssignUserModal from '~/ui/pages/ActTeam/reusable/AssignUserModal';
import ClinicDictionaries from '~/ui/pages/Dictionaries/ClinicDictionaries';
import { IUserRole } from '~/types';
import useLocalAdminConfirmProps from '../hooks/useLocalAdminConfirmProps';
import ActTeam from './ActTeam';
import AiClinicSettings from './AiClinicSettings';
import ClinicProfile from './ClinicProfile';
import Header from './Header';
import LocalAdminTable from './LocalAdmin/Table';
import LocationGroups from './LocationGroups';
import ProgramAssistant from './ProgramAssistant';
import Shelters from './Shelters';

type IParams = {
  id: string;
};

type ILocationState = { state: { selectedTab: number } };

const dictionaryTabs: Record<number, ICustomDictionaryType> = {
  6: documentTypeDictionary,
  7: dispositionDictionary,
  8: medicaidTypeDictionary,
  9: exemptionCategoryDictionary,
};

// Dictionary tabs shown only while the clinic has Medicaid Expansion enabled.
const medicaidDictionaryTabs = [8, 9];

const ViewClinic = (): ReactElement => {
  const { id } = useParams<IParams>();
  const location: ILocationState = useLocation();

  const [tab, setTab] = useState(location.state?.selectedTab || 0);
  const [loading, setLoading] = useState(false);
  const [localAdmin, setLocalAdmin] = useState(null);
  const [assignModalVisible, setAssignModalVisible] = useState(false);

  const dictionaryItemAction = useDictionaryItemAction();

  const current = useStoreState(state => state.clinic.current);
  const localAdminPagination = useStoreState(state => state.localAdmin.pagination);
  const programAssistantPagination = useStoreState(state => state.programAssistant.pagination);
  const { isOpenAiAvailable, isAiAssistAvailable, isMedicaidAvailable } = useFeature();

  const { showError } = useStoreActions(actions => actions.snackbar);
  const onGetClinic = useStoreActions(actions => actions.clinic.onGetClinic);
  const onGetProgramAssistants = useStoreActions(
    actions => actions.programAssistant.onGetProgramAssistants,
  );
  const { onGetLocalAdmins } = useStoreActions(actions => actions.localAdmin);

  const onMount = useCallback(async () => {
    setLoading(true);
    try {
      await onGetClinic(id);
      setLoading(false);
    } catch (e) {
      showError(extractErrorMessage(e));
    }
  }, [id, onGetClinic, showError]);

  const refetchUsers = useCallback(async () => {
    if (tab === 2) {
      await onGetLocalAdmins({
        clinicId: id,
        params: {
          pageNumber: localAdminPagination.pageNumber,
          pageSize: localAdminPagination.pageSize,
        },
      });
    } else {
      await onGetProgramAssistants({
        clinicId: id,
        params: {
          pageNumber: programAssistantPagination.pageNumber,
          pageSize: programAssistantPagination.pageSize,
        },
      });
    }
  }, [
    tab,
    onGetLocalAdmins,
    id,
    localAdminPagination.pageNumber,
    localAdminPagination.pageSize,
    onGetProgramAssistants,
    programAssistantPagination.pageNumber,
    programAssistantPagination.pageSize,
  ]);

  useEffect(() => {
    onMount();
  }, [onMount]);

  const { confirmText, description, method } = useLocalAdminConfirmProps(localAdmin, refetchUsers);

  const activeDictionary =
    medicaidDictionaryTabs.includes(tab) && !isMedicaidAvailable ? undefined : dictionaryTabs[tab];

  const onTabChange = (value: number): void => {
    dictionaryItemAction.onClose();
    setTab(value);
  };

  if (loading || !current) return <Loader />;

  const onConfirm = async () => {
    try {
      await method();
      setLocalAdmin(false);
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setLocalAdmin(false);
    }
  };

  return (
    <div>
      <Header
        name={current.name}
        tab={tab}
        setTab={onTabChange}
        id={id}
        currentTab={tab}
        addDictionaryLabel={activeDictionary && getAddLabel(activeDictionary)}
        openAssignModal={() => setAssignModalVisible(true)}
        onAddDictionaryItem={dictionaryItemAction.onAddDictionaryItem}
      />
      {tab === 0 && <ClinicProfile clinic={current} />}
      {tab === 1 && <ActTeam />}
      {tab === 2 && <LocalAdminTable setLocalAdmin={setLocalAdmin} />}
      {tab === 3 && <ProgramAssistant />}
      {tab === 4 && <Shelters />}
      {tab === 5 && <LocationGroups />}
      {activeDictionary && (
        <ClinicDictionaries
          key={activeDictionary.type}
          defaultDictionaryType={activeDictionary}
          itemAction={dictionaryItemAction}
          hideHeader
        />
      )}
      {tab === 10 && (isOpenAiAvailable || isAiAssistAvailable) && <AiClinicSettings />}
      {localAdmin && (
        <ConfirmModal
          description={description}
          confirmText={confirmText}
          onClose={() => setLocalAdmin(null)}
          onConfirm={onConfirm}
        />
      )}
      {assignModalVisible && (
        <AssignUserModal
          modalTitle={
            tab === 2
              ? 'Search User to assign Local Admin Role'
              : 'Search Program Assistant to assign to this Team '
          }
          role={(tab === 2 ? IUserRole.LocalAdmin : IUserRole.ProgramAssistant).replace(/\s/g, '')}
          onClose={() => setAssignModalVisible(null)}
          refetchUsers={refetchUsers}
        />
      )}
    </div>
  );
};

export default ViewClinic;
